/**
 * ⚠️ This hook must be used inside a Client Component.
 * It relies on `window`, which is not available during SSR.
 */
"use client";

import { useEffect, useState } from "react";

/**
 * Custom React hook to track the current URL hash (`window.location.hash`).
 *
 * This hook:
 * - Returns the current hash value (e.g. "#create-list")
 * - Automatically updates when the hash changes
 * - Works only in client-side components
 *
 * @example
 * const hash = useHash();
 *
 * if (hash === "#create-list") {
 *   console.log("Create list mode active");
 * }
 *
 * @returns {string} The current URL hash including the "#" symbol.
 */
export function useHash(): string {
  const [hash, setHash] = useState<string>("");

  useEffect(() => {
    /**
     * Updates the hash state with the current window location hash.
     */
    const updateHash = () => {
      setHash(window.location.hash);
    };

    // Set initial hash on mount
    updateHash();

    // Listen for hash changes
    window.addEventListener("hashchange", updateHash);

    return () => {
      window.removeEventListener("hashchange", updateHash);
    };
  }, []);

  return hash;
}